import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaUser, FaUserPlus, FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';
import Footer from '../components/Footer';
import { formatPhoneBR, isValidEmail, isValidPhoneBR, onlyDigits, normalizeEmail } from '../utils/formatters';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, var(--background) 0%, #e9ecef 100%);
  display: flex;
  flex-direction: column;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
    padding-top: 40px;
  }
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentCard = styled.div`
  background: var(--surface);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 500px;
  position: relative;

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: 16px;
    max-width: 100%;
  }
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--background);
  color: var(--text-primary);
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover { background: var(--primary); color: var(--accent); }

  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    padding: 6px 12px;
    font-size: 14px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  img {
    width: 80px; height: 80px; border-radius: 50%; margin: 0 auto 20px; box-shadow: 0 4px 16px rgba(212,175,55,0.3);
  }
  h1 { font-size: 2rem; font-weight: 700; color: var(--primary); margin-bottom: 8px; }
  p { color: var(--text-secondary); font-size: 1.1rem; }

  @media (max-width: 768px) {
    margin-bottom: 30px;
    h1 { font-size: 1.8rem; }
    p { font-size: 1rem; }
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: var(--background);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 30px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: ${props => props.active ? 'var(--surface)' : 'transparent'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  &:hover { color: #20AC9F; }
`;

const Form = styled.form`
  .form-group { margin-bottom: 20px; }
  .form-label { display: block; margin-bottom: 8px; font-weight: 500; color: var(--text-primary); }

  .form-input {
    width: 100%; padding: 14px 16px; border: 2px solid var(--border); border-radius: 10px; font-size: 16px; transition: all 0.2s; background: var(--surface);
  }
  .form-input:focus { outline: none; border-color: var(--secondary); box-shadow: 0 0 0 3px rgba(212,175,55,0.1); }
  .form-input.error { border-color: var(--error); }

  .form-error { color: var(--error); font-size: 14px; margin-top: 4px; }

  .submit-btn {
    width: 100%; padding: 16px; background: #20AC9F; color: var(--accent); border: none; border-radius: 10px;
    font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; margin-top: 20px;
  }
  .submit-btn:hover:not(:disabled) { background: #1A8C7F; transform: translateY(-2px); box-shadow: 0 4px 16px rgba(26,26,26,0.3); }
  .submit-btn:disabled { opacity: .6; cursor: not-allowed; }

  @media (max-width: 768px) {
    .form-group { margin-bottom: 18px; }
    .form-label { font-size: 14px; margin-bottom: 6px; }
    .form-input { padding: 12px 14px; font-size: 16px; }
    .submit-btn { padding: 14px; font-size: 16px; margin-top: 18px; }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block; width: 20px; height: 20px; border: 3px solid rgba(255,255,255,.3);
  border-radius: 50%; border-top-color: var(--accent); animation: spin 1s ease-in-out infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ClientLoginPage = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', data_nascimento: '' });
  const [errors, setErrors] = useState({});

  const { loginClient } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'data_nascimento') {
      formatted = value.replace(/[^\d-]/g, '').slice(0, 10);
    } else if (name === 'phone') {
      formatted = formatPhoneBR(value);
    } else if (name === 'email') {
      formatted = normalizeEmail(value);
    }

    setFormData(prev => ({ ...prev, [name]: formatted }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (activeTab === 'register') {
      if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
      // Data de nascimento obrigatória e não pode ser default
      if (!formData.data_nascimento || formData.data_nascimento === '1900-01-01') {
        newErrors.data_nascimento = 'Data de nascimento é obrigatória';
      } else {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(formData.data_nascimento)) {
          newErrors.data_nascimento = 'Formato de data inválido (YYYY-MM-DD)';
        }
      }
      const phoneDigits = onlyDigits(formData.phone);
      if (!phoneDigits) newErrors.phone = 'Telefone é obrigatório';
      else if (!isValidPhoneBR(phoneDigits)) newErrors.phone = 'Telefone inválido';
      if (formData.email && !isValidEmail(formData.email)) newErrors.email = 'Email inválido';
    } else {
      const phoneDigits = onlyDigits(formData.phone);
      if (!phoneDigits) newErrors.phone = 'Informe o telefone';
      else if (!(phoneDigits.length === 10 || phoneDigits.length === 11)) newErrors.phone = 'Informe um telefone válido (10-11 dígitos)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
  const phoneDigits = onlyDigits(formData.phone);
  const response = await api.post('/clients/login', { identifier: phoneDigits });
      loginClient(response.data);
      navigate('/cliente/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao fazer login';
      toast.error(message);
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await api.post('/clients/', {
        name: formData.name.trim(),
        data_nascimento: formData.data_nascimento,
        phone: onlyDigits(formData.phone),
        email: formData.email ? normalizeEmail(formData.email) : null,
      });
      loginClient(response.data);
      navigate('/cliente/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao cadastrar';
      toast.error(message);
    } finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
    if (activeTab === 'login') handleLogin(e); else handleRegister(e);
  };

  return (
    <PageContainer>
      <Main>
        <ContentCard>
          <BackButton to="/">
            <FaArrowLeft />
            Voltar
          </BackButton>

          <Header>
            <img src="/logo.png" alt="Matheus Barber Logo" />
            <h1>Matheus Barber</h1>
            <p>Área do Cliente</p>
          </Header>

          <TabContainer>
            <Tab active={activeTab === 'login'} onClick={() => setActiveTab('login')}>
              <FaUser /> Login
            </Tab>
            <Tab active={activeTab === 'register'} onClick={() => setActiveTab('register')}>
              <FaUserPlus /> Cadastro
            </Tab>
          </TabContainer>

          <Form onSubmit={handleSubmit}>
            {activeTab === 'register' && (
              <div className="form-group">
                <label className="form-label">Nome Completo</label>
                <input type="text" name="name" className={`form-input ${errors.name ? 'error' : ''}`} value={formData.name} onChange={handleInputChange} placeholder="Digite seu nome completo" />
                {errors.name && <div className="form-error">{errors.name}</div>}
              </div>
            )}

            {activeTab === 'register' && (
              <div className="form-group">
                <label className="form-label">Data de Nascimento</label>
                <input type="date" name="data_nascimento" className={`form-input ${errors.data_nascimento ? 'error' : ''}`} value={formData.data_nascimento} onChange={handleInputChange} required />
                {errors.data_nascimento && <div className="form-error">{errors.data_nascimento}</div>}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Telefone</label>
              <input type="tel" name="phone" className={`form-input ${errors.phone ? 'error' : ''}`} value={formData.phone} onChange={handleInputChange} placeholder="Digite seu telefone" />
              {errors.phone && <div className="form-error">{errors.phone}</div>}
            </div>

            {activeTab === 'register' && (
              <div className="form-group">
                <label className="form-label">Email (opcional)</label>
                <input type="email" name="email" className="form-input" value={formData.email} onChange={handleInputChange} placeholder="Digite seu email" />
                {errors.email && <div className="form-error">{errors.email}</div>}
              </div>
            )}

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (<><LoadingSpinner /> {activeTab === 'login' ? 'Entrando...' : 'Cadastrando...'}</>) : (activeTab === 'login' ? 'Entrar' : 'Cadastrar')}
            </button>
          </Form>
        </ContentCard>
      </Main>
      <Footer />
    </PageContainer>
  );
};

export default ClientLoginPage;
